// AddReadings.jsx
import React, { useEffect, useState } from "react";
import normalize, { fuzzyIncludes } from "../utils/normalize";

const AddReadings = () => {
  const [substations, setSubstations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [meters, setMeters] = useState([]);
  const [readings, setReadings] = useState([]);

  const [selectedSubstation, setSelectedSubstation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValues, setInputValues] = useState({});
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    // Загружаем подстанции, сети, счётчики и показания
    Promise.all([
      fetch("/api/lilsubstations", { headers }),
      fetch("/api/lilnetworks", { headers }),
      fetch("/api/meters", { headers }),
      fetch("/api/meterreadings", { headers }),
    ])
      .then((responses) => Promise.all(responses.map((r) => r.json())))
      .then(([subs, nets, mets, reads]) => {
        setSubstations(subs);
        setNetworks(nets);
        setMeters(mets);
        setReadings(reads);
      })
      .catch((err) => console.error("Ошибка при загрузке:", err));
  }, []);

  // Фильтрация подстанций (с транслитерацией)
  const filteredSubstations = substations.filter((sub) =>
    fuzzyIncludes(normalize(sub.name), searchQuery)
  );

  // Функции определения "приходящих" / "исходящих" линий (как в SubstationsOverview)
  const getIncomingLines = (subId) => {
    return networks.filter((net) => {
      if (net.to_substation === subId) return true;
      if (net.mode === "twoway" && net.from_substation === subId) return true;
      return false;
    });
  };

  const getOutgoingLines = (subId) => {
    return networks.filter((net) => {
      if (net.from_substation === subId) return true;
      if (net.mode === "twoway" && net.to_substation === subId) return true;
      return false;
    });
  };

  // Сабмит с проверкой "один раз в день" по meterId
  const validateAndSubmit = async () => {
    const token = localStorage.getItem("token");
    const today = new Date().toISOString().slice(0, 10);

    const newErrors = {};
    const payloads = [];

    // inputValues — объект вида { "9": "123.00", "10": "456.00", ... }
    // если мы используем key=`meter.id`, а direction не включаем
    for (const [meterId, valRaw] of Object.entries(inputValues)) {
      const val = valRaw.trim();

      // Проверяем, есть ли уже запись за today для meterId
      const alreadyExists = readings.some(
        (r) => r.meter === parseInt(meterId) && r.createdAt?.slice(0, 10) === today
      );

      if (!val || isNaN(val)) {
        newErrors[meterId] = "Введите корректное число";
      } else if (alreadyExists) {
        newErrors[meterId] = "Сегодня уже было добавлено";
      } else {
        // Формируем payload без direction (т.к. в meterreadings нет поля direction)
        payloads.push({ meter: meterId, value: val });
      }
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setSuccessMessage("");
      return;
    }

    try {
      // Отправляем все новые показания
      await Promise.all(
        payloads.map((data) =>
          fetch("/api/meterreadings", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          })
        )
      );
      setSuccessMessage("Показания успешно добавлены");
      setInputValues({});
      setErrors({});

      // Перезагружаем readings, чтобы следующая попытка на сегодня блокировалась
      const res = await fetch("/api/meterreadings", { headers: { Authorization: `Bearer ${token}` } });
      const updatedReads = await res.json();
      setReadings(updatedReads);

    } catch (err) {
      console.error("Ошибка при отправке показаний:", err);
    }
  };

  // Рендер строки (network + meter), без direction в `meterreadings`.
  // Мы показываем meter.direction только визуально, ведь в самой БД meterreadings нет поля direction.
  const renderInputRow = (network, meter) => {
    if (!meter) {
      // Если нет счётчика - ставим "—"
      return (
        <tr key={`net${network.id}-nometer`}>
          <td>{network.name}</td>
          <td>—</td>
          <td>—</td>
        </tr>
      );
    }

    const key = String(meter.id); // используем meter.id как ключ
    return (
      <tr key={key}>
        <td>
          {network.name}{" "}
          {meter.direction && `(${meter.direction})`}
          {/* Просто для отображения */}
        </td>
        <td>{meter.number}</td>
        <td>
          <input
            type="text"
            className={errors[key] ? "input-error" : ""}
            value={inputValues[key] || ""}
            onChange={(e) =>
              setInputValues((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
          />
          {errors[key] && <div className="error-text">{errors[key]}</div>}
        </td>
      </tr>
    );
  };

  // Рендер таблицы "incoming" или "outgoing"
  const renderLinesTable = (subId, lines, directionType) => {
    return (
      <table className="networks-table">
        <thead>
          <tr>
            <th>Линия</th>
            <th>Счётчик</th>
            <th>Показание</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((network) => {
            // Определяем, нужны ли счётчики forward/reverse для этой подстанции
            // как в SubstationsOverview
            let neededDirection;
            if (network.from_substation === subId) {
              if (directionType === "outgoing") {
                neededDirection = "forward";
              } else {
                neededDirection = "reverse";
              }
            } else if (network.to_substation === subId) {
              if (directionType === "incoming") {
                neededDirection = "forward";
              } else {
                neededDirection = "reverse";
              }
            }

            // Фильтруем те Meter, что относятся к network + subId + нужный direction
            const relevantMeters = meters.filter(
              (m) =>
                m.network_id === network.id &&
                m.substation_id === subId &&
                m.direction === neededDirection
            );

            if (!relevantMeters.length) {
              // Если нет ни одного счётчика (на этой стороне) -> рендерим одну пустую строку
              return renderInputRow(network, null);
            }

            return relevantMeters.map((meter) => renderInputRow(network, meter));
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="add-readings-page">
      <div className="container">
        <h1>Добавление показаний</h1>

        <input
          type="text"
          placeholder="Поиск подстанции..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="branch-search"
        />

        <ul className="branch-list">
          {filteredSubstations.map((sub) => (
            <li key={sub.id} onClick={() => setSelectedSubstation(sub)}>
              {sub.name}
            </li>
          ))}
        </ul>

        {selectedSubstation && (
          <div className="networks-table-wrapper">
            <h2>{selectedSubstation.name}</h2>

            {/* Приходящие линии */}
            <h4>Приходящие линии</h4>
            {renderLinesTable(
              selectedSubstation.id,
              getIncomingLines(selectedSubstation.id),
              "incoming"
            )}

            {/* Исходящие линии */}
            <h4>Исходящие линии</h4>
            {renderLinesTable(
              selectedSubstation.id,
              getOutgoingLines(selectedSubstation.id),
              "outgoing"
            )}

            <button onClick={validateAndSubmit} className="submit-button">
              Отправить
            </button>
            {successMessage && <div className="success-text">{successMessage}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddReadings;
